import { z } from 'zod';
import { idSchema } from './ids';

/**
 * The app's own mail folder for messages kept here that sit in no mailbox
 * folder (history brought in with the clinic). Listed like a folder, but no
 * IMAP path takes this name, so the mailbox never syncs into it.
 */
export const EXTERNAL_FOLDER = '\\External';

export const OUTBOUND_ATTACHMENT_MAX_COUNT = 10;
/**
 * Per attachment *and* per email, which MailerSend's own rules make the same
 * number: they document 25MB for one attachment, measured after Base64
 * decoding, and no total at all.
 *
 * Decimal, not `25 * 1024 * 1024`: they never say which "MB" they mean, and
 * being over their line means a clean submit followed by a silent refusal.
 *
 * Measured raw bytes and what they became on the wire:
 *   25,197,281  ->  32.0 MiB base64  delivered
 *   34,063,608  ->  43.3 MiB base64  never reached the provider — building the
 *                                    request hung, and a hung send records no
 *                                    failure at all
 */
export const OUTBOUND_ATTACHMENT_MAX_BYTES = 25_000_000;
export const OUTBOUND_BODY_MAX_BYTES = 512 * 1024;
// MailerSend's own recipient caps, mirrored exactly: anything we reject that the
// provider would accept is our invention, and anything it rejects we want to
// catch at submit rather than at send.
export const OUTBOUND_TO_MAX_COUNT = 50;
export const OUTBOUND_CC_MAX_COUNT = 10;
export const OUTBOUND_BCC_MAX_COUNT = 10;
export const OUTBOUND_SUBJECT_MAX_LENGTH = 998;

/**
 * Where an HTML body lives: on the row when it is small, in storage when it is
 * not. One constant, imported by every writer, so every ingest path splits the
 * corpus the same way.
 */
export const HTML_INLINE_MAX_BYTES = 128 * 1024;

/**
 * Three states, three shapes. Not two nullable fields — that is four
 * combinations for three legal states, and the illegal one is the drift a
 * threshold invites.
 */
export const emailBodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('inline'), html: z.string() }),
  z.object({ kind: z.literal('stored'), storageId: idSchema('files') }),
  z.object({ kind: z.literal('none') }),
]);

export type EmailBody = z.infer<typeof emailBodySchema>;

/** Case-folded for comparison, never for identity. Rejects nothing. */
export function normalizeEmailCase(value: string) {
  return value.trim().toLowerCase();
}

// Every address gate uses this, and it is not zod's default: over 2,736 distinct
// addresses the two disagreed on exactly one, and the default was on the wrong
// side of it — rejecting `imię.nazwisko59@gmail.com`, a real sender with a unicode
// local part. A bare `includes('@')` is the other extreme: it admits
// `bozenadanrnbeg012@gemlcom`.
export const EMAIL_ADDRESS_PATTERN = z.regexes.rfc5322Email;

/** An address as an identity. Branded, so only this can produce a value the stored
 *  channel fields accept. */
export const emailKeySchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ pattern: EMAIL_ADDRESS_PATTERN }))
  .brand<'EmailKey'>();

export type EmailKey = z.infer<typeof emailKeySchema>;

const outboundClientRequestIdMaxLength = 128;
const outboundAttachmentFieldMaxLength = 998;
const outboundEmailAddressMaxLength = 320;
const outboundDisplayNameMaxLength = 998;

export const emailAddressSchema = z.strictObject({
  email: z.email({ pattern: EMAIL_ADDRESS_PATTERN }),
  name: z.string().optional(),
});

export const storedEmailAttachmentSchema = z.strictObject({
  contentId: z.string().nullable(),
  contentType: z.string(),
  disposition: z.enum(['inline', 'attachment']).nullable(),
  filename: z.string(),
  size: z.number().nullable(),
  storageId: idSchema('files'),
});

// Delivery is per recipient because reality is per recipient. `soft_bounced`
// stays distinct from `hard_bounced`: hard is documented terminal, soft is
// retried with no terminal event, so collapsing them invents certainty.
export const emailDeliveryStatusSchema = z.enum([
  'pending',
  'sent',
  'delivered',
  'soft_bounced',
  'hard_bounced',
]);

export const emailDeliverySchema = z.strictObject({
  address: z.string(),
  at: z.number().optional(),
  kind: z.enum(['to', 'cc', 'bcc']),
  providerEmailId: z.string().optional(),
  status: emailDeliveryStatusSchema,
});

export const outboundEmailAddressSchema = z.strictObject({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(outboundEmailAddressMaxLength)
    .pipe(z.email({ pattern: EMAIL_ADDRESS_PATTERN })),
  name: z.string().trim().min(1).max(outboundDisplayNameMaxLength).optional(),
});

export const outboundClientRequestIdSchema = z
  .string()
  .min(1)
  .max(outboundClientRequestIdMaxLength);

export const outboundAttachmentManifestSchema = z.strictObject({
  filename: z.string().trim().min(1).max(outboundAttachmentFieldMaxLength),
});

const outboundToSchema = z
  .array(outboundEmailAddressSchema)
  .min(1)
  .max(OUTBOUND_TO_MAX_COUNT);

const outboundDraftSchema = z.strictObject({
  bcc: z
    .array(outboundEmailAddressSchema)
    .max(OUTBOUND_BCC_MAX_COUNT)
    .default([]),
  cc: z
    .array(outboundEmailAddressSchema)
    .max(OUTBOUND_CC_MAX_COUNT)
    .default([]),
  html: z.string().min(1).optional(),
  text: z.string(),
});

const outboundPrepareInputBaseSchema = outboundDraftSchema.safeExtend({
  attachments: z
    .array(outboundAttachmentManifestSchema)
    .max(OUTBOUND_ATTACHMENT_MAX_COUNT)
    .default([]),
  clientRequestId: outboundClientRequestIdSchema,
});

export const outboundPrepareInputSchema = outboundPrepareInputBaseSchema
  .extend({
    mode: z.discriminatedUnion('kind', [
      z.strictObject({
        kind: z.literal('new'),
        subject: z.string().trim().min(1).max(OUTBOUND_SUBJECT_MAX_LENGTH),
        to: outboundToSchema,
      }),
      z.strictObject({
        kind: z.literal('reply'),
        emailId: idSchema('emails'),
        all: z.boolean().default(false),
      }),
      z.strictObject({
        kind: z.literal('forward'),
        emailId: idSchema('emails'),
        to: outboundToSchema,
      }),
    ]),
  })
  .superRefine((input, ctx) => {
    if (input.mode.kind !== 'forward' && !input.text.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Message body is required.',
        path: ['text'],
      });
    }
  });

export const outboundResolvedMessageSchema = outboundDraftSchema.safeExtend({
  from: outboundEmailAddressSchema,
  inReplyTo: z.string().optional(),
  references: z.array(z.string()).default([]),
  replyTo: outboundEmailAddressSchema,
  subject: z.string().min(1).max(OUTBOUND_SUBJECT_MAX_LENGTH),
  text: z.string().min(1),
  to: outboundToSchema,
});

// A send in flight: composed, not yet an email. It carries the resolved message,
// the idempotency anchor, the attachments it will travel with, and the failure
// record. Backs the `outbound_sends` table (`Id<'outboundSends'>`).
export const outboundEmailReceiptSchema =
  outboundResolvedMessageSchema.safeExtend({
    // The note the receptionist typed, not the composed whole. A forward
    // composes its quote while sending, where the source body in storage is
    // reachable, so the note stands alone here — and an empty one is legal,
    // because a forward may carry no note at all. `resolveOutboundMessage`
    // holds the "a new message or a reply must say something" line instead.
    text: z.string(),
    attachments: z
      .array(storedEmailAttachmentSchema)
      .max(OUTBOUND_ATTACHMENT_MAX_COUNT)
      .default([]),
    clientRequestId: outboundClientRequestIdSchema,
    emailId: idSchema('emails').optional(),
    failedAt: z.number().optional(),
    failureReason: z.string().optional(),
    forwardedFromEmailId: idSchema('emails').optional(),
    providerMessageId: z.string().optional(),
    sentBy: z.string(),
    workflowId: z.string().optional(),
  });

// The provider-neutral fields available before persistence adds mailbox state
// such as direction, placement, provider ids, and the resolved thread id.
export const emailContentSchema = z.strictObject({
  attachments: z.array(storedEmailAttachmentSchema),
  bcc: z.array(emailAddressSchema),
  cc: z.array(emailAddressSchema),
  createdAt: z.number(),
  from: emailAddressSchema,
  body: emailBodySchema,
  // Threading headers are stored verbatim and never rewritten. Canonicalisation
  // happens only where a key is derived, never on the stored value.
  inReplyTo: z.string().nullable(),
  messageId: z.string().nullable(),
  references: z.array(z.string()),
  replyTo: emailAddressSchema.optional(),
  subject: z.string(),
  // The text as it went on the wire: the provider's text part inbound, what we
  // composed outbound. Null when the sender sent no text part at all. Never
  // derived and never trimmed — a field that sometimes held the sender's words
  // and sometimes our extraction of them would make the two indistinguishable
  // the next time we improve the walk.
  originalText: z.string().nullable(),
  // Always populated: `originalText` with the quoted chain and the signature
  // taken out, or the `htmlToText` walk when there is no original. This is what
  // the message says, so previews, summaries and the feed read it.
  strippedText: z.string(),
  to: z.array(emailAddressSchema),
});

export const emailDocSchema = emailContentSchema.extend({
  // Bounded by MailerSend's recipient caps (50 to + 10 cc + 10 bcc), so this is
  // a bounded array rather than a child table.
  deliveries: z.array(emailDeliverySchema),
  // AI summary of this one message.
  description: z.string().optional(),
  direction: z.enum(['inbound', 'outbound']),
  forwardedFromEmailId: idSchema('emails').optional(),
  placement: z.enum(['active', 'spam', 'trash']),
  // MailerSend payload data.id: one value per inbound message. The top-level
  // inbound_id identifies the route and cannot be used for retry dedup.
  providerInboundId: z.string().optional(),
  // Better Auth user id of the sender; only set on outbound emails.
  sentBy: z.string().optional(),
  threadId: idSchema('emailThreads'),
});

export const outboundEmailMaterializationSchema = z
  .object({
    ...outboundEmailReceiptSchema.shape,
    body: emailBodySchema,
    createdAt: z.number(),
    messageId: z.string(),
    // What was actually handed to the provider. For a forward this is
    // the note with the quoted original composed onto it; for anything else it
    // is the note unchanged.
    sentText: z.string(),
    threadId: idSchema('emailThreads'),
  })
  .transform(
    ({
      clientRequestId: _clientRequestId,
      emailId: _emailId,
      failedAt: _failedAt,
      failureReason: _failureReason,
      html: _html,
      providerMessageId: _providerMessageId,
      workflowId: _workflowId,
      inReplyTo,
      sentText,
      text,
      ...send
    }): z.input<typeof emailDocSchema> => ({
      ...send,
      deliveries: seedDeliveries(send),
      direction: 'outbound',
      inReplyTo: inReplyTo ?? null,
      placement: 'active',
      // Mail we send is never parsed: the composer knows exactly what was typed
      // and exactly what was quoted onto it, so both fields are set from what is
      // already in hand. A note-less forward falls back to the composed whole
      // for the same reason a message that strips to nothing is kept whole —
      // an empty stripped text is not a truthful summary of anything.
      originalText: sentText,
      strippedText: text.trim() || sentText,
    }),
  )
  .pipe(emailDocSchema);

// The inbound wire content, before ingest stores the HTML body and derives the
// stripped text. `emailContentSchema` is its stored counterpart.
export const inboundEmailContentSchema = z.strictObject({
  bcc: z.array(emailAddressSchema),
  cc: z.array(emailAddressSchema),
  createdAt: z.number(),
  from: emailAddressSchema,
  html: z.string().nullable(),
  inReplyTo: z.string().nullable(),
  messageId: z.string().nullable(),
  references: z.array(z.string()),
  replyTo: emailAddressSchema.optional(),
  subject: z.string(),
  text: z.string().nullable(),
  to: z.array(emailAddressSchema),
});

export const inboundEmailThreadDraftSchema = z.strictObject({
  subject: z.string(),
});

export type EmailAddress = z.infer<typeof emailAddressSchema>;
export type EmailContent = z.infer<typeof emailContentSchema>;
export type EmailDelivery = z.infer<typeof emailDeliverySchema>;
export type InboundEmailContent = z.infer<typeof inboundEmailContentSchema>;

export function getOutboundBodyByteLength(input: {
  html?: string;
  text: string;
}) {
  const encoder = new TextEncoder();
  return (
    encoder.encode(input.text).byteLength +
    (input.html ? encoder.encode(input.html).byteLength : 0)
  );
}

// Every recipient starts pending; each provider event patches only its own
// entry, so out-of-order events across recipients stop mattering.
function seedDeliveries(recipients: {
  bcc: Array<{ email: string }>;
  cc: Array<{ email: string }>;
  to: Array<{ email: string }>;
}): Array<z.input<typeof emailDeliverySchema>> {
  return (['to', 'cc', 'bcc'] as const).flatMap((kind) =>
    recipients[kind].map(({ email }) => ({
      address: email,
      kind,
      status: 'pending' as const,
    })),
  );
}
